// ===========================================
// HOOKS: Auditoría (CORE)
// ===========================================

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useSupabase } from '@/lib/supabase-context'
import { auditoriaService } from '../services/auditoriaService'
import type { FiltrosAuditoria, EntidadAuditoria } from '../types/auditoria'

// ===========================================
// Query Keys
// ===========================================

export const auditoriaKeys = {
  all: ['auditoria'] as const,
  lista: (filtros?: FiltrosAuditoria, limite?: number) =>
    [...auditoriaKeys.all, 'lista', filtros, limite] as const,
  entidad: (entidad: EntidadAuditoria, entidadId: string) =>
    [...auditoriaKeys.all, 'entidad', entidad, entidadId] as const,
  usuario: (email: string) => [...auditoriaKeys.all, 'usuario', email] as const,
  workspace: (workspace: string) => [...auditoriaKeys.all, 'workspace', workspace] as const,
  estadisticas: (dias?: number, workspace?: string) =>
    [...auditoriaKeys.all, 'estadisticas', dias, workspace] as const,
}

// ===========================================
// Hooks de Query
// ===========================================

/**
 * Hook para listar logs de auditoría
 */
export function useAuditoria(filtros?: FiltrosAuditoria, limite = 100) {
  const { supabase, isLoading: supabaseLoading } = useSupabase()

  return useQuery({
    queryKey: auditoriaKeys.lista(filtros, limite),
    queryFn: () => auditoriaService.listar(supabase!, filtros, limite),
    enabled: !!supabase && !supabaseLoading,
  })
}

/**
 * Hook para obtener logs de una entidad específica
 */
export function useAuditoriaEntidad(entidad: EntidadAuditoria, entidadId: string) {
  const { supabase, isLoading: supabaseLoading } = useSupabase()

  return useQuery({
    queryKey: auditoriaKeys.entidad(entidad, entidadId),
    queryFn: () => auditoriaService.obtenerPorEntidad(supabase!, entidad, entidadId),
    enabled: !!supabase && !supabaseLoading && !!entidad && !!entidadId,
  })
}

/**
 * Hook para obtener logs de un usuario
 */
export function useAuditoriaUsuario(email: string, limite = 50) {
  const { supabase, isLoading: supabaseLoading } = useSupabase()

  return useQuery({
    queryKey: auditoriaKeys.usuario(email),
    queryFn: () => auditoriaService.obtenerPorUsuario(supabase!, email, limite),
    enabled: !!supabase && !supabaseLoading && !!email,
  })
}

/**
 * Hook para obtener logs de un workspace
 */
export function useAuditoriaWorkspace(workspace: string, limite = 100) {
  const { supabase, isLoading: supabaseLoading } = useSupabase()

  return useQuery({
    queryKey: auditoriaKeys.workspace(workspace),
    queryFn: () => auditoriaService.obtenerPorWorkspace(supabase!, workspace, limite),
    enabled: !!supabase && !supabaseLoading && !!workspace,
  })
}

/**
 * Hook para obtener estadísticas de auditoría
 */
export function useEstadisticasAuditoria(dias = 30, workspace?: string) {
  const { supabase, isLoading: supabaseLoading } = useSupabase()

  return useQuery({
    queryKey: auditoriaKeys.estadisticas(dias, workspace),
    queryFn: () => auditoriaService.obtenerEstadisticas(supabase!, dias, workspace),
    enabled: !!supabase && !supabaseLoading,
  })
}

// ===========================================
// Hooks de Mutation
// ===========================================

/**
 * Hook para exportar logs a CSV
 */
export function useExportarAuditoria() {
  const { supabase } = useSupabase()

  return useMutation({
    mutationFn: async ({
      filtros,
      limite,
      filename,
    }: {
      filtros?: FiltrosAuditoria
      limite?: number
      filename?: string
    }) => {
      if (!supabase) throw new Error('Supabase no disponible')
      const logs = await auditoriaService.listar(supabase, filtros, limite || 1000)
      auditoriaService.descargarCSV(logs, filename)
      return logs.length
    },
  })
}

/**
 * Hook para refrescar auditoría
 */
export function useRefrescarAuditoria() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async () => {
      await queryClient.invalidateQueries({ queryKey: auditoriaKeys.all })
    },
  })
}

// ===========================================
// Re-exports
// ===========================================

export type {
  AuditLog,
  AuditLogConUsuario,
  AuditLogInsert,
  FiltrosAuditoria,
  AccionAuditoria,
  EntidadAuditoria,
  EstadisticasAuditoria,
} from '../types/auditoria'

export {
  ACCION_CONFIG,
  ENTIDAD_CONFIG,
  ACCIONES_OPTIONS,
  ENTIDADES_OPTIONS,
  formatearDetalleAuditoria,
} from '../types/auditoria'
